import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
    Container, Grid, Typography, TextField, Box,
    Button, Accordion, AccordionSummary, AccordionActions,
    Dialog, DialogTitle, DialogActions, DialogContent,
    FormControlLabel, RadioGroup, Radio, FormControl,
    Select, MenuItem, InputLabel, IconButton, Divider,
    Chip, Stack, Tabs, Tab, AppBar, Toolbar, Tooltip,
    Paper, Card, CardContent, LinearProgress, Autocomplete, Link, Checkbox,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import VisibilityIcon from '@mui/icons-material/Visibility';
import WarningIcon from '@mui/icons-material/Warning';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import 'katex/dist/katex.min.css';
import { InlineMath } from 'react-katex';
import globals from '../globals';
import { useAuth } from '../context/AuthContext';
import RedButton from '../components/redButton';
import ExamLanding from '../exam_page/ExamLanding';

// ---------------------------------------------------------------------------
// Preview helpers
// ---------------------------------------------------------------------------

function quizTypeFromApi(type) {
    if (type === 'multiple_choice') return 'single';
    if (type === 'multi_part') return 'multi';
    return 'file';
}

// Case-insensitive, whitespace-trimmed comparison for free-text answers.
function answersMatch(given, expected) {
    return String(given ?? '').trim().toLowerCase() === String(expected ?? '').trim().toLowerCase();
}

function convertToQuizFormat(examDetail) {
    return {
        id: String(examDetail.id),
        title: examDetail.title,
        description: examDetail.description,
        introTitle: examDetail.intro_title,
        introDescription: examDetail.intro_description,
        guidelines: examDetail.guidelines || [],
        sections: (examDetail.sections || []).map(s => ({
            title: s.title || '',
            durationMinutes: s.duration_minutes || null,
            breakAfterMinutes: s.break_after_minutes || 0,
            questions: (s.questions || []).map(q => ({
                id: String(q.id),
                type: quizTypeFromApi(q.type),
                text: q.text || '',
                imageSrc: q.image_url || null,
                points: q.points || 1,
                options: (q.options || []).map(o => ({ id: String(o.id), label: o.text })),
                correctOptionId: q.correct_option_id ? String(q.correct_option_id) : '',
                subquestions: (q.subquestions || []).map(sq => ({
                    id: String(sq.id),
                    text: sq.text || '',
                    correctAnswer: sq.correct_answer || '',
                })),
            })),
        })),
    };
}

function renderWithLatex(text) {
    if (!text || !text.includes('$')) return text;
    return text.split('$').map((part, i) =>
        i % 2 === 1
            ? <InlineMath key={i} math={part} />
            : <React.Fragment key={i}>{part}</React.Fragment>
    );
}

function SectionedExamQuiz({ examData }) {
    const sections = examData.sections || [];

    const [phase, setPhase] = useState('intro'); // 'intro' | 'section' | 'break' | 'completed'
    const [sectionIdx, setSectionIdx] = useState(0);
    const [questionIdx, setQuestionIdx] = useState(0);
    const [selectedByQId, setSelectedByQId] = useState({});
    const [filesByQId, setFilesByQId] = useState({});
    const [textBySubKey, setTextBySubKey] = useState({}); // `${qId}::${subId}` -> answer
    const [remainingSeconds, setRemainingSeconds] = useState(null);
    const [breakSecondsLeft, setBreakSecondsLeft] = useState(0);

    const currentSection = sections[sectionIdx] || null;
    const questions = currentSection?.questions || [];
    const currentQuestion = questions[questionIdx] || null;

    // Section timer
    useEffect(() => {
        if (phase !== 'section' || remainingSeconds === null || remainingSeconds <= 0) return;
        const id = setInterval(() => setRemainingSeconds(s => (s <= 1 ? 0 : s - 1)), 1000);
        return () => clearInterval(id);
    }, [phase, remainingSeconds]);

    useEffect(() => {
        if (phase === 'section' && remainingSeconds === 0) advanceAfterSection();
    }, [remainingSeconds, phase]); // eslint-disable-line react-hooks/exhaustive-deps

    // Break timer
    useEffect(() => {
        if (phase !== 'break' || breakSecondsLeft <= 0) return;
        const id = setInterval(() => setBreakSecondsLeft(s => (s <= 1 ? 0 : s - 1)), 1000);
        return () => clearInterval(id);
    }, [phase, breakSecondsLeft]);

    useEffect(() => {
        if (phase === 'break' && breakSecondsLeft === 0) startSection(sectionIdx + 1);
    }, [breakSecondsLeft, phase]); // eslint-disable-line react-hooks/exhaustive-deps

    function startSection(idx) {
        setSectionIdx(idx);
        setQuestionIdx(0);
        setPhase('section');
        const dur = sections[idx]?.durationMinutes;
        setRemainingSeconds(dur ? dur * 60 : null);
    }

    function advanceAfterSection() {
        const isLast = sectionIdx >= sections.length - 1;
        if (isLast) {
            setPhase('completed');
            return;
        }
        const breakMins = sections[sectionIdx]?.breakAfterMinutes || 0;
        if (breakMins > 0) {
            setPhase('break');
            setBreakSecondsLeft(breakMins * 60);
        } else {
            startSection(sectionIdx + 1);
        }
    }

    function isAnswered(q) {
        if (q.type === 'multi') {
            return (q.subquestions || []).length > 0 &&
                q.subquestions.every(sq => (textBySubKey[`${q.id}::${sq.id}`] || '').trim());
        }
        return Boolean(selectedByQId[q.id]) ||
            (Array.isArray(filesByQId[q.id]) && filesByQId[q.id].length > 0);
    }

    const remainingMin = remainingSeconds !== null ? Math.floor(remainingSeconds / 60) : null;
    const remainingSecPart = remainingSeconds !== null ? remainingSeconds % 60 : null;

    // ——— INTRO ———
    if (phase === 'intro') {
        return (
            <Container sx={{ py: 6 }}>
                <Grid container direction="column" spacing={3}>
                    <Grid item>
                        <Typography variant="h4">{examData.title}</Typography>
                        <Typography variant="body1" color="text.secondary">{examData.description}</Typography>
                    </Grid>
                    <Grid item>
                        <Card elevation={2}>
                            <CardContent>
                                <Grid container direction="column" spacing={2}>
                                    <Grid item>
                                        <Typography variant="h6">
                                            <WarningIcon sx={{ mb: -0.4, mr: 1 }} />
                                            {examData.introTitle || examData.title}
                                        </Typography>
                                    </Grid>
                                    <Grid item>
                                        <Typography variant="body1" sx={{ color: globals.colors.darkRed, fontWeight: 'bold' }}>
                                            {examData.introDescription || examData.description}
                                        </Typography>
                                    </Grid>
                                    {examData.guidelines?.length > 0 && (
                                        <Grid item>
                                            <Box component="ul" sx={{ pl: 3, m: 0 }}>
                                                {examData.guidelines.map((g, i) => (
                                                    <Typography key={i} component="li" variant="body2" sx={{ mb: 0.5 }}>{g}</Typography>
                                                ))}
                                            </Box>
                                        </Grid>
                                    )}
                                    <Grid item>
                                        <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
                                            <Grid item>
                                                <RedButton text="Започни изпита" onClick={() => startSection(0)} />
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </CardContent>
                        </Card>
                    </Grid>
                </Grid>
            </Container>
        );
    }

    // ——— BREAK ———
    if (phase === 'break') {
        const breakMin = Math.floor(breakSecondsLeft / 60);
        const breakSec = breakSecondsLeft % 60;
        return (
            <Container maxWidth="sm" sx={{ py: 6 }}>
                <Card elevation={3}>
                    <CardContent>
                        <Grid container direction="column" spacing={2} alignItems="center" textAlign="center">
                            <Grid item>
                                <Typography variant="h5" sx={{ fontWeight: 'bold' }}>Почивка</Typography>
                            </Grid>
                            <Grid item>
                                <Typography variant="body1" color="text.secondary">
                                    Следва раздел {sectionIdx + 2} от {sections.length}:{' '}
                                    <strong>{sections[sectionIdx + 1]?.title}</strong>
                                </Typography>
                            </Grid>
                            <Grid item>
                                <Typography variant="h3" sx={{ fontWeight: 'bold', color: globals.colors.darkBlue }}>
                                    {breakMin}:{String(breakSec).padStart(2, '0')}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Следващият раздел започва автоматично
                                </Typography>
                            </Grid>
                            <Grid item>
                                <RedButton text="Продължи сега" onClick={() => { setBreakSecondsLeft(0); startSection(sectionIdx + 1); }} />
                            </Grid>
                        </Grid>
                    </CardContent>
                </Card>
            </Container>
        );
    }

    // ——— COMPLETED ———
    if (phase === 'completed') {
        let totalCorrect = 0, totalUnanswered = 0, totalEarned = 0, totalMax = 0;
        const totalQs = sections.reduce((sum, s) => sum + s.questions.length, 0);
        sections.forEach(sec => {
            sec.questions.forEach(q => {
                const pts = q.points || 1;
                totalMax += pts;
                if (q.type === 'multi') {
                    const subs = q.subquestions || [];
                    const correctSubs = subs.filter(sq =>
                        answersMatch(textBySubKey[`${q.id}::${sq.id}`], sq.correctAnswer)).length;
                    if (!isAnswered(q)) { totalUnanswered += 1; }
                    if (subs.length && correctSubs === subs.length) { totalCorrect += 1; }
                    totalEarned += subs.length ? Math.round((pts * correctSubs) / subs.length) : 0;
                    return;
                }
                const selected = selectedByQId[q.id];
                const files = filesByQId[q.id];
                const answered = Boolean(selected) || (Array.isArray(files) && files.length > 0);
                if (!answered) { totalUnanswered += 1; return; }
                if (q.type !== 'file' && selected === q.correctOptionId) { totalCorrect += 1; totalEarned += pts; }
                else if (q.type === 'file') { totalCorrect += 1; totalEarned += pts; }
            });
        });
        return (
            <Container sx={{ py: 6 }}>
                <Card elevation={1}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>Резултат от симулацията</Typography>
                        <Typography variant="body1">Верни отговори: {totalCorrect} от {totalQs}</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Неотговорени: {totalUnanswered}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                            Точки: {totalEarned} от {totalMax}
                        </Typography>
                    </CardContent>
                </Card>
            </Container>
        );
    }

    // ——— ACTIVE SECTION ———
    return (
        <Container sx={{ py: 6 }}>
            <Grid container direction="column" spacing={3}>
                <Grid item>
                    <Typography variant="h5">{examData.title}</Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                        Раздел {sectionIdx + 1} от {sections.length}
                        {currentSection?.title ? `: ${currentSection.title}` : ''}
                    </Typography>
                    {remainingSeconds !== null && (
                        <Typography variant="h6" sx={{
                            mt: 0.5, fontWeight: 'bold',
                            color: remainingSeconds <= 60 ? 'error.main' : globals.colors.darkBlue,
                        }}>
                            Оставащо време: {remainingMin}:{String(remainingSecPart).padStart(2, '0')}
                        </Typography>
                    )}
                </Grid>

                {currentQuestion && (
                    <Grid item>
                        <Grid container spacing={3} alignItems="flex-start">
                            <Grid item xs={12} md={3}>
                                <Card elevation={1} sx={{ position: { xs: 'static', md: 'sticky' }, top: { md: 96 } }}>
                                    <CardContent>
                                        <Typography variant="subtitle2" color="text.secondary" gutterBottom>Задачи</Typography>
                                        <Grid container direction="column" spacing={1}>
                                            {questions.map((q, i) => {
                                                const answered = isAnswered(q);
                                                const active = i === questionIdx;
                                                return (
                                                    <Grid item key={q.id}>
                                                        <Button
                                                            variant={answered || active ? 'contained' : 'outlined'}
                                                            size="small"
                                                            onClick={() => setQuestionIdx(i)}
                                                            sx={{
                                                                justifyContent: 'space-between',
                                                                textTransform: 'none',
                                                                width: '100%',
                                                                ...(answered
                                                                    ? { bgcolor: globals.colors.darkBlue, color: '#fff', '&:hover': { bgcolor: globals.colors.lightBlue, color: '#fff' } }
                                                                    : { bgcolor: active ? globals.colors.gray : 'transparent', color: active ? '#fff' : globals.colors.gray, borderColor: !active ? globals.colors.gray : undefined }),
                                                            }}
                                                        >
                                                            <Typography variant="body2">Задача {i + 1}</Typography>
                                                        </Button>
                                                    </Grid>
                                                );
                                            })}
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={9}>
                                <Card elevation={3}>
                                    <CardContent>
                                        <Grid container direction="column" spacing={2}>
                                            <Grid item>
                                                <Grid container justifyContent="space-between" alignItems="center">
                                                    <Grid item>
                                                        <Typography variant="subtitle2" color="text.secondary">
                                                            Задача {questionIdx + 1} от {questions.length}
                                                        </Typography>
                                                    </Grid>
                                                    <Grid item>
                                                        <Box sx={{ minWidth: 160 }}>
                                                            <LinearProgress variant="determinate"
                                                                value={((questionIdx + 1) / questions.length) * 100} />
                                                        </Box>
                                                    </Grid>
                                                </Grid>
                                            </Grid>

                                            <Grid item>
                                                {currentQuestion.imageSrc && (
                                                    <Box component="img" src={currentQuestion.imageSrc}
                                                        alt="Илюстрация"
                                                        sx={{ maxWidth: '100%', height: 'auto', mb: 2, borderRadius: 1 }} />
                                                )}
                                                <Typography variant="h6">
                                                    {renderWithLatex(currentQuestion.text)}
                                                    {typeof currentQuestion.points === 'number' && (
                                                        <Typography component="span" variant="subtitle2"
                                                            color="text.secondary" sx={{ ml: 1 }}>
                                                            ({currentQuestion.points} т.)
                                                        </Typography>
                                                    )}
                                                </Typography>
                                            </Grid>

                                            {currentQuestion.type === 'multi' && (
                                                <Grid item>
                                                    <Grid container direction="column" spacing={2}>
                                                        {currentQuestion.subquestions.map((sq, si) => {
                                                            const key = `${currentQuestion.id}::${sq.id}`;
                                                            return (
                                                                <Grid item key={sq.id}>
                                                                    <Typography variant="body1" sx={{ mb: 0.5 }}>
                                                                        {si + 1}. {renderWithLatex(sq.text)}
                                                                    </Typography>
                                                                    <TextField
                                                                        size="small"
                                                                        fullWidth
                                                                        placeholder="Твоят отговор"
                                                                        value={textBySubKey[key] ?? ''}
                                                                        onChange={e => setTextBySubKey(prev => ({ ...prev, [key]: e.target.value }))}
                                                                    />
                                                                </Grid>
                                                            );
                                                        })}
                                                    </Grid>
                                                </Grid>
                                            )}

                                            {currentQuestion.type === 'single' && (
                                                <Grid item>
                                                    <RadioGroup
                                                        value={selectedByQId[currentQuestion.id] ?? ''}
                                                        onChange={e => setSelectedByQId(prev => ({ ...prev, [currentQuestion.id]: e.target.value }))}
                                                    >
                                                        {currentQuestion.options.map(opt => {
                                                            const selected = selectedByQId[currentQuestion.id] === opt.id;
                                                            return (
                                                                <Box key={opt.id} sx={{
                                                                    mb: 1, borderRadius: 1, borderWidth: 1,
                                                                    borderStyle: 'solid',
                                                                    borderColor: selected ? 'primary.main' : 'divider',
                                                                    '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
                                                                }}>
                                                                    <FormControlLabel
                                                                        value={opt.id}
                                                                        control={<Radio color="primary" />}
                                                                        label={<Typography variant="body1">{renderWithLatex(opt.label)}</Typography>}
                                                                        sx={{ pl: 1.5, pr: 1, py: 0.5, width: '100%', m: 0 }}
                                                                    />
                                                                </Box>
                                                            );
                                                        })}
                                                    </RadioGroup>
                                                </Grid>
                                            )}

                                            {currentQuestion.type === 'file' && (
                                                <Grid item>
                                                    <Typography variant="subtitle2">
                                                        Качи файлове (pdf, png, jpeg, jpg, heic)
                                                    </Typography>
                                                    <Button variant="outlined" component="label" sx={{ mt: 1 }}>
                                                        Избери файлове
                                                        <input hidden type="file" multiple accept=".pdf,.png,.jpeg,.jpg,.heic"
                                                            onChange={e => {
                                                                const files = e.target.files ? Array.from(e.target.files) : [];
                                                                setFilesByQId(prev => ({ ...prev, [currentQuestion.id]: files }));
                                                            }} />
                                                    </Button>
                                                    {Array.isArray(filesByQId[currentQuestion.id]) &&
                                                        filesByQId[currentQuestion.id].map(f => (
                                                            <Typography key={f.name + f.lastModified} variant="body2" color="text.secondary">
                                                                {f.name}
                                                            </Typography>
                                                        ))}
                                                </Grid>
                                            )}

                                            <Grid item>
                                                <Grid container justifyContent="space-between" spacing={2}>
                                                    <Grid item>
                                                        <Button variant="outlined"
                                                            disabled={questionIdx === 0}
                                                            onClick={() => setQuestionIdx(i => i - 1)}>
                                                            Назад
                                                        </Button>
                                                    </Grid>
                                                    <Grid item>
                                                        {questionIdx < questions.length - 1 ? (
                                                            <RedButton text="Напред" onClick={() => setQuestionIdx(i => i + 1)} />
                                                        ) : (
                                                            <RedButton
                                                                text={sectionIdx < sections.length - 1 ? 'Завърши раздела' : 'Предай изпита'}
                                                                onClick={advanceAfterSection}
                                                            />
                                                        )}
                                                    </Grid>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>
                        </Grid>
                    </Grid>
                )}
            </Grid>
        </Container>
    );
}

function SessionPagePreview({ exam }) {
    const isOnDemand = exam.exam_type === 'ON_DEMAND';
    const startDateObj = exam.start_date ? new Date(exam.start_date) : null;
    const dateStr = startDateObj
        ? startDateObj.toLocaleDateString('bg-BG', { day: '2-digit', month: '2-digit', year: 'numeric' })
        : '';
    const timeStr = startDateObj
        ? startDateObj.toLocaleTimeString('bg-BG', { hour: '2-digit', minute: '2-digit' })
        : '';

    return (
        <Container maxWidth="md" sx={{ py: 6 }}>
            <Grid container direction="column" spacing={3}>
                <Grid item>
                    <Typography variant="h4" component="h1" gutterBottom>
                        {exam.title}
                    </Typography>
                </Grid>
                <Grid item>
                    <Card elevation={2}>
                        <CardContent>
                            <Grid container direction="column" spacing={2}>
                                <Grid item>
                                    <Typography variant="h6">
                                        <WarningIcon sx={{ mb: -0.4, mr: 1 }} />
                                        {exam.title}
                                    </Typography>
                                </Grid>
                                <Grid item>
                                    {isOnDemand ? (
                                        <Typography variant="body1" sx={{ color: globals.colors.darkBlue, fontWeight: 'bold', fontSize: 17 }}>
                                            Изпитът е достъпен по всяко време. Можеш да го започнеш когато си готов.
                                        </Typography>
                                    ) : (
                                        <Typography variant="body1" sx={{ color: globals.colors.darkBlue, fontWeight: 'bold', fontSize: 17 }}>
                                            Изпитът ще се отключи на <strong>{dateStr}</strong> в <strong>{timeStr}ч.</strong> Моля, влез в платформата навреме и се увери, че имаш стабилна интернет връзка и включена камера в Zoom.
                                        </Typography>
                                    )}
                                </Grid>
                                <Grid item>
                                    <Grid container justifyContent="flex-end" sx={{ mt: 1 }}>
                                        <Grid item>
                                            {isOnDemand ? (
                                                <RedButton text="Започни изпита" onClick={() => {}} />
                                            ) : (
                                                <Tooltip title="Изпитът ще бъде достъпен от датата на провеждане">
                                                    <span>
                                                        <RedButton text="Започни изпита" disabled onClick={() => {}} />
                                                    </span>
                                                </Tooltip>
                                            )}
                                        </Grid>
                                    </Grid>
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                </Grid>
            </Grid>
        </Container>
    );
}

function ExamPreviewDialog({ open, examId, token, onClose }) {
    const [tab, setTab] = useState(0);
    const [examDetail, setExamDetail] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) return;
        setTab(0);
        setExamDetail(null);
        setError('');
        if (!examId) return;
        setLoading(true);
        fetch(`${globals.apiUrl}/admin/exams/${examId}`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(r => r.json())
            .then(data => { setExamDetail(data); setLoading(false); })
            .catch(() => { setError('Грешка при зареждане'); setLoading(false); });
    }, [open, examId, token]);

    const quizData = useMemo(() => {
        if (!examDetail) return null;
        return convertToQuizFormat(examDetail);
    }, [examDetail]);

    return (
        <Dialog open={open} onClose={onClose} fullScreen>
            <AppBar sx={{ position: 'relative', backgroundColor: globals.colors.darkBlue }}>
                <Toolbar>
                    <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
                        <CloseIcon />
                    </IconButton>
                    <Typography sx={{ ml: 2, flex: 1 }} variant="h6" noWrap>
                        Преглед: {examDetail?.title || '...'}
                    </Typography>
                </Toolbar>
            </AppBar>

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs value={tab} onChange={(_, v) => setTab(v)} centered>
                    <Tab label="Публична страница" />
                    <Tab label="Страница след покупка" />
                    <Tab label="Симулация на изпита" />
                </Tabs>
            </Box>

            <Box sx={{ overflow: 'auto', flexGrow: 1 }}>
                {loading ? (
                    <Box sx={{ p: 4 }}>
                        <Typography gutterBottom>Зареждане...</Typography>
                        <LinearProgress />
                    </Box>
                ) : error ? (
                    <Box sx={{ p: 4 }}>
                        <Typography color="error">{error}</Typography>
                    </Box>
                ) : examDetail ? (
                    <>
                        {tab === 0 && <ExamLanding exam={examDetail} onBuy={() => {}} hideSticky />}
                        {tab === 1 && <SessionPagePreview exam={examDetail} />}
                        {tab === 2 && quizData && <SectionedExamQuiz examData={quizData} />}
                    </>
                ) : null}
            </Box>
        </Dialog>
    );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const EMPTY_OPTION = () => ({ id: String(Date.now() + Math.random()), text: '' });

const EMPTY_SUBQUESTION = () => ({ id: String(Date.now() + Math.random()), text: '', correct_answer: '' });

const EMPTY_QUESTION = () => ({
    _key: String(Date.now() + Math.random()),
    order_index: 0,
    text: '',
    type: 'multiple_choice',
    points: 1,
    image_url: '',
    options: [EMPTY_OPTION(), EMPTY_OPTION()],
    correct_option_id: '',
    subquestions: [EMPTY_SUBQUESTION()],
});

const EMPTY_SECTION = () => ({
    _key: String(Date.now() + Math.random()),
    order_index: 0,
    title: '',
    duration_minutes: 60,
    break_after_minutes: '',
    description: '',
    topics: [],
    questions: [EMPTY_QUESTION()],
});

const SUBJECT_LABELS = {
    BEL: 'БЕЛ',
    MATH: 'Математика',
};

const EMPTY_FORM = {
    instructor_id: '',
    grader_ids: [],
    subject: '',
    title: '',
    description: '',
    intro_title: '',
    intro_description: '',
    guidelines: [''],
    price: '',
    exam_type: 'SCHEDULED',
    start_date: '',
    image_url: '',
    meta_description: '',
    what_is_text: '',
    highlights: [''],
    coverage_summary: '',
    has_raffle: false,
    instructor_bios: {},
    instructor_courses: {},
    sections: [],
};

function toApiDateTime(localStr) {
    if (!localStr) return null;
    return new Date(localStr).toISOString();
}

function toLocalDateTimeInput(isoStr) {
    if (!isoStr) return '';
    const d = new Date(isoStr);
    const pad = n => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function formFromExam(exam) {
    return {
        instructor_id: exam.instructor_id || '',
        grader_ids: exam.grader_ids || [],
        subject: exam.subject || '',
        title: exam.title || '',
        description: exam.description || '',
        intro_title: exam.intro_title || '',
        intro_description: exam.intro_description || '',
        guidelines: exam.guidelines?.length ? exam.guidelines : [''],
        price: exam.price ?? '',
        exam_type: exam.exam_type || 'SCHEDULED',
        start_date: toLocalDateTimeInput(exam.start_date),
        image_url: exam.image_url || '',
        meta_description: exam.meta_description || '',
        what_is_text: exam.what_is_text || '',
        highlights: exam.highlights?.length ? exam.highlights : [''],
        coverage_summary: exam.coverage_summary || '',
        has_raffle: Boolean(exam.has_raffle),
        instructor_bios: exam.instructor_bios || {},
        instructor_courses: exam.instructor_courses || {},
        sections: (exam.sections || []).map(s => ({
            _key: String(s.id || Date.now() + Math.random()),
            order_index: s.order_index,
            title: s.title || '',
            duration_minutes: s.duration_minutes,
            break_after_minutes: s.break_after_minutes ?? '',
            description: s.description || '',
            topics: s.topics || [],
            questions: (s.questions || []).map(q => ({
                _key: String(q.id || Date.now() + Math.random()),
                order_index: q.order_index,
                text: q.text || '',
                type: q.type || 'multiple_choice',
                points: q.points ?? 1,
                image_url: q.image_url || '',
                options: q.options?.length ? q.options.map(o => ({ id: o.id, text: o.text })) : [EMPTY_OPTION(), EMPTY_OPTION()],
                correct_option_id: q.correct_option_id || '',
                subquestions: q.subquestions?.length
                    ? q.subquestions.map(sq => ({ id: String(sq.id), text: sq.text || '', correct_answer: sq.correct_answer || '' }))
                    : [EMPTY_SUBQUESTION()],
            })),
        })),
    };
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

function TextListEditor({ items, onChange, title, itemLabel, addLabel, minItems = 1 }) {
    const update = (i, val) => {
        const next = [...items];
        next[i] = val;
        onChange(next);
    };
    const add = () => onChange([...items, '']);
    const remove = (i) => onChange(items.filter((_, idx) => idx !== i));

    return (
        <Box>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>{title}</Typography>
            {items.map((item, i) => (
                <Box key={i} sx={{ display: 'flex', gap: 1, mb: 1 }}>
                    <TextField
                        size="small" fullWidth value={item}
                        onChange={e => update(i, e.target.value)}
                        placeholder={`${itemLabel} ${i + 1}`}
                    />
                    <IconButton size="small" onClick={() => remove(i)} disabled={items.length <= minItems}>
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={add}>{addLabel}</Button>
        </Box>
    );
}

function GuidelinesEditor({ guidelines, onChange }) {
    return (
        <TextListEditor items={guidelines} onChange={onChange}
            title="Насоки (guidelines)" itemLabel="Насока" addLabel="Добави насока" />
    );
}

function OptionEditor({ options, correctId, onChange, onCorrectChange }) {
    const update = (i, field, val) => {
        const next = options.map((o, idx) => idx === i ? { ...o, [field]: val } : o);
        onChange(next);
    };
    const add = () => onChange([...options, EMPTY_OPTION()]);
    const remove = (i) => {
        const next = options.filter((_, idx) => idx !== i);
        onChange(next);
        if (options[i]?.id === correctId) onCorrectChange('');
    };

    return (
        <Box sx={{ ml: 2 }}>
            <Typography variant="caption">Отговори</Typography>
            {options.map((opt, i) => (
                <Box key={opt.id} sx={{ display: 'flex', gap: 1, mb: 0.5, alignItems: 'center' }}>
                    <Radio
                        size="small"
                        checked={correctId === opt.id}
                        onChange={() => onCorrectChange(opt.id)}
                    />
                    <TextField
                        size="small" value={opt.text} fullWidth
                        placeholder={`Отговор ${i + 1}`}
                        onChange={e => update(i, 'text', e.target.value)}
                    />
                    <IconButton size="small" onClick={() => remove(i)} disabled={options.length <= 2}>
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={add}>Добави отговор</Button>
        </Box>
    );
}

function SubQuestionEditor({ subquestions, onChange }) {
    const update = (i, field, val) => {
        onChange(subquestions.map((sq, idx) => idx === i ? { ...sq, [field]: val } : sq));
    };
    const add = () => onChange([...subquestions, EMPTY_SUBQUESTION()]);
    const remove = (i) => onChange(subquestions.filter((_, idx) => idx !== i));

    return (
        <Box sx={{ ml: 2 }}>
            <Typography variant="caption">Подвъпроси (всеки със свой отговор)</Typography>
            {subquestions.map((sq, i) => (
                <Box key={sq.id} sx={{ display: 'flex', gap: 1, mb: 0.5, alignItems: 'center' }}>
                    <Typography variant="body2" sx={{ minWidth: 20 }}>{i + 1}.</Typography>
                    <TextField
                        size="small" value={sq.text} fullWidth
                        placeholder={`Подвъпрос ${i + 1} (напр. x*2 = 8)`}
                        onChange={e => update(i, 'text', e.target.value)}
                    />
                    <TextField
                        size="small" value={sq.correct_answer} sx={{ maxWidth: 160 }}
                        placeholder="Верен отговор"
                        onChange={e => update(i, 'correct_answer', e.target.value)}
                    />
                    <IconButton size="small" onClick={() => remove(i)} disabled={subquestions.length <= 1}>
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            ))}
            <Button size="small" startIcon={<AddIcon />} onClick={add}>Добави подвъпрос</Button>
        </Box>
    );
}

function ImageUploadField({ imageUrl, onUrlChange, token, examId, size = 'small' }) {
    const [uploading, setUploading] = useState(false);

    const handleFile = async (file) => {
        if (!file) return;
        setUploading(true);
        const fd = new FormData();
        fd.append('file', file);
        const qs = examId ? `?exam_id=${examId}` : '';
        try {
            const res = await fetch(`${globals.apiUrl}/admin/exams/images/upload${qs}`, {
                method: 'POST',
                headers: { Authorization: `Bearer ${token}` },
                body: fd,
            });
            const data = await res.json();
            if (data.url) onUrlChange(data.url);
        } catch {}
        setUploading(false);
    };

    return (
        <Box>
            <Button
                component="label"
                size={size}
                variant="outlined"
                fullWidth
                disabled={uploading}
                startIcon={<UploadFileIcon />}
            >
                {uploading ? 'Качване...' : imageUrl ? 'Смени снимката' : 'Качи снимка'}
                <input hidden type="file" accept="image/*"
                    onChange={e => handleFile(e.target.files?.[0])} />
            </Button>
            {imageUrl && (
                <Box sx={{ mt: 1, position: 'relative', display: 'inline-flex', alignItems: 'flex-start' }}>
                    <img src={imageUrl} alt="preview"
                        style={{ maxWidth: '100%', maxHeight: size === 'small' ? 100 : 180, borderRadius: 4, display: 'block' }} />
                    <IconButton size="small" onClick={() => onUrlChange('')}
                        sx={{ position: 'absolute', top: 2, right: 2, bgcolor: 'rgba(255,255,255,0.85)', p: 0.5 }}>
                        <DeleteIcon fontSize="small" />
                    </IconButton>
                </Box>
            )}
        </Box>
    );
}

function QuestionEditor({ question, onUpdate, onRemove, token, examId }) {
    const set = (field, val) => onUpdate({ ...question, [field]: val });

    return (
        <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 2, mb: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="caption" sx={{ fontWeight: 'bold' }}>Въпрос</Typography>
                <IconButton size="small" onClick={onRemove}><DeleteIcon fontSize="small" /></IconButton>
            </Box>

            <Grid container spacing={1}>
                <Grid item xs={12}>
                    <TextField size="small" fullWidth label="Текст на въпроса" value={question.text}
                        onChange={e => set('text', e.target.value)} />
                </Grid>
                <Grid item xs={4}>
                    <FormControl size="small" fullWidth>
                        <InputLabel>Тип</InputLabel>
                        <Select value={question.type} label="Тип" onChange={e => set('type', e.target.value)}>
                            <MenuItem value="multiple_choice">Избор</MenuItem>
                            <MenuItem value="file_upload">Отворен отговор (снимка)</MenuItem>
                            <MenuItem value="multi_part">Съставна задача</MenuItem>
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={4}>
                    <TextField size="small" fullWidth type="number" label="Точки" value={question.points}
                        onChange={e => set('points', Number(e.target.value))} />
                </Grid>
                <Grid item xs={4}>
                    <ImageUploadField
                        imageUrl={question.image_url}
                        onUrlChange={url => set('image_url', url)}
                        token={token}
                        examId={examId}
                    />
                </Grid>
            </Grid>

            {question.type === 'multiple_choice' && (
                <Box sx={{ mt: 1 }}>
                    <OptionEditor
                        options={question.options}
                        correctId={question.correct_option_id}
                        onChange={opts => set('options', opts)}
                        onCorrectChange={id => set('correct_option_id', id)}
                    />
                </Box>
            )}

            {question.type === 'multi_part' && (
                <Box sx={{ mt: 1 }}>
                    <SubQuestionEditor
                        subquestions={question.subquestions || []}
                        onChange={subs => set('subquestions', subs)}
                    />
                </Box>
            )}
        </Box>
    );
}

function SectionEditor({ section, onUpdate, onRemove, token, examId }) {
    const set = (field, val) => onUpdate({ ...section, [field]: val });

    const updateQuestion = (i, q) => {
        const next = section.questions.map((qq, idx) => idx === i ? q : qq);
        set('questions', next);
    };
    const addQuestion = () => set('questions', [...section.questions, EMPTY_QUESTION()]);
    const removeQuestion = (i) => set('questions', section.questions.filter((_, idx) => idx !== i));

    return (
        <Box sx={{ border: '1px solid #bbb', borderRadius: 1, p: 2, mb: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>Раздел</Typography>
                <IconButton size="small" onClick={onRemove}><DeleteIcon /></IconButton>
            </Box>

            <Grid container spacing={1} sx={{ mb: 2 }}>
                <Grid item xs={12}>
                    <TextField size="small" fullWidth label="Заглавие" value={section.title}
                        onChange={e => set('title', e.target.value)} />
                </Grid>
                <Grid item xs={6}>
                    <TextField size="small" fullWidth type="number" label="Продължителност (мин)" value={section.duration_minutes}
                        onChange={e => set('duration_minutes', Number(e.target.value))} />
                </Grid>
                <Grid item xs={6}>
                    <TextField size="small" fullWidth type="number" label="Почивка след (мин)" value={section.break_after_minutes}
                        onChange={e => set('break_after_minutes', e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                    <TextField size="small" fullWidth multiline minRows={2} label="Описание на модула (страница на изпита)"
                        value={section.description}
                        onChange={e => set('description', e.target.value)} />
                </Grid>
                <Grid item xs={12}>
                    <TextListEditor items={section.topics} onChange={t => set('topics', t)} minItems={0}
                        title="Теми (страница на изпита)" itemLabel="Тема" addLabel="Добави тема" />
                </Grid>
            </Grid>

            <Divider sx={{ mb: 2 }} />

            {section.questions.map((q, i) => (
                <QuestionEditor
                    key={q._key}
                    question={q}
                    onUpdate={updated => updateQuestion(i, updated)}
                    onRemove={() => removeQuestion(i)}
                    token={token}
                    examId={examId}
                />
            ))}

            <Button size="small" startIcon={<AddIcon />} onClick={addQuestion}>Добави въпрос</Button>
        </Box>
    );
}

function ExamFormDialog({ open, examId, token, onClose, onSaved }) {
    const [form, setForm] = useState(EMPTY_FORM);
    const [instructors, setInstructors] = useState([]);
    const [courses, setCourses] = useState([]);
    const [loading, setLoading] = useState(false);
    const [fetchLoading, setFetchLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (!open) return;
        fetch(`${globals.apiUrl}/admin/instructors`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(r => r.json())
            .then(data => setInstructors(Array.isArray(data) ? data : []))
            .catch(() => {});
        fetch(`${globals.apiUrl}/admin/courses`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(r => r.json())
            .then(data => setCourses(Array.isArray(data) ? data : []))
            .catch(() => {});
    }, [open, token]);

    useEffect(() => {
        if (!open) return;
        if (examId) {
            setFetchLoading(true);
            fetch(`${globals.apiUrl}/admin/exams/${examId}`, {
                headers: { Authorization: `Bearer ${token}` },
            })
                .then(r => r.json())
                .then(data => { setForm(formFromExam(data)); setFetchLoading(false); })
                .catch(() => setFetchLoading(false));
        } else {
            setForm(EMPTY_FORM);
        }
        setError('');
    }, [open, examId, token]);

    const set = (field, val) => setForm(f => ({ ...f, [field]: val }));

    const addSection = () => set('sections', [...form.sections, EMPTY_SECTION()]);
    const updateSection = (i, s) => set('sections', form.sections.map((ss, idx) => idx === i ? s : ss));
    const removeSection = (i) => set('sections', form.sections.filter((_, idx) => idx !== i));

    const buildPayload = () => ({
        instructor_id: Number(form.instructor_id),
        grader_ids: form.grader_ids.filter(id => id !== Number(form.instructor_id)),
        subject: form.subject || null,
        title: form.title,
        description: form.description,
        intro_title: form.intro_title,
        intro_description: form.intro_description,
        guidelines: form.guidelines.filter(g => g.trim()),
        price: Number(form.price),
        exam_type: form.exam_type,
        start_date: form.exam_type === 'SCHEDULED' ? toApiDateTime(form.start_date) : null,
        image_url: form.image_url || null,
        meta_description: form.meta_description.trim() || null,
        what_is_text: form.what_is_text.trim() || null,
        highlights: form.highlights.map(h => h.trim()).filter(Boolean),
        coverage_summary: form.coverage_summary.trim().replace(/\.$/, '') || null,
        has_raffle: form.has_raffle,
        instructor_bios: form.instructor_bios,
        instructor_courses: form.instructor_courses,
        sections: form.sections.map((s, si) => ({
            order_index: si,
            title: s.title,
            duration_minutes: Number(s.duration_minutes),
            break_after_minutes: s.break_after_minutes !== '' ? Number(s.break_after_minutes) : null,
            description: s.description.trim() || null,
            topics: s.topics.map(t => t.trim()).filter(Boolean),
            questions: s.questions.map((q, qi) => ({
                order_index: qi,
                text: q.text,
                type: q.type,
                points: Number(q.points),
                image_url: q.image_url || null,
                options: q.type === 'multiple_choice' ? q.options : null,
                correct_option_id: q.type === 'multiple_choice' ? q.correct_option_id : null,
                subquestions: q.type === 'multi_part'
                    ? q.subquestions.filter(sq => sq.text.trim())
                    : null,
            })),
        })),
    });

    const handleSave = async () => {
        if (!form.instructor_id) { setError('Избери инструктор'); return; }
        setError('');
        setLoading(true);
        const payload = buildPayload();
        // Both create and edit go through admin endpoints.
        const url = examId
            ? `${globals.apiUrl}/admin/exams/${examId}`
            : `${globals.apiUrl}/admin/exams`;
        const method = examId ? 'PUT' : 'POST';
        try {
            const res = await fetch(url, {
                method,
                headers: {
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(payload),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                setError(err.detail || 'Грешка при запис');
                setLoading(false);
                return;
            }
            setLoading(false);
            onSaved();
            onClose();
        } catch {
            setError('Грешка при свързване');
            setLoading(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>{examId ? 'Редактирай изпит' : 'Създай изпит'}</DialogTitle>
            <DialogContent dividers>
                {fetchLoading ? (
                    <Typography>Зареждане...</Typography>
                ) : (
                    <Grid container spacing={2}>
                        <Grid item xs={12}>
                            <FormControl fullWidth required>
                                <InputLabel>Създател *</InputLabel>
                                <Select value={form.instructor_id} label="Създател *"
                                    onChange={e => set('instructor_id', e.target.value)}>
                                    {instructors.map(inst => (
                                        <MenuItem key={inst.id} value={inst.id}>
                                            {inst.first_name} {inst.last_name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid item xs={12}>
                            <Autocomplete
                                multiple
                                options={instructors.filter(inst => inst.id !== form.instructor_id)}
                                getOptionLabel={inst => `${inst.first_name} ${inst.last_name}`}
                                value={instructors.filter(inst => form.grader_ids.includes(inst.id) && inst.id !== form.instructor_id)}
                                onChange={(e, selected) => set('grader_ids', selected.map(inst => inst.id))}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                renderInput={(params) => (
                                    <TextField {...params} label="Оценяващи преподаватели"
                                        helperText="Създателят и оценяващите могат да проверяват предадените работи" />
                                )}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Предмет</InputLabel>
                                <Select value={form.subject} label="Предмет"
                                    onChange={e => set('subject', e.target.value)}>
                                    <MenuItem value=""><em>Без предмет</em></MenuItem>
                                    {Object.entries(SUBJECT_LABELS).map(([value, label]) => (
                                        <MenuItem key={value} value={value}>{label}</MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12}>
                            <TextField fullWidth label="Заглавие *" value={form.title}
                                onChange={e => set('title', e.target.value)} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth multiline rows={3} label="Описание *" value={form.description}
                                helperText="Показва се под заглавието на страницата на изпита"
                                onChange={e => set('description', e.target.value)} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth label="Intro заглавие *" value={form.intro_title}
                                onChange={e => set('intro_title', e.target.value)} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth label="Intro описание *" value={form.intro_description}
                                onChange={e => set('intro_description', e.target.value)} />
                        </Grid>
                        <Grid item xs={6}>
                            <TextField fullWidth type="number" label="Цена (EUR) *" value={form.price}
                                onChange={e => set('price', e.target.value)} />
                        </Grid>
                        <Grid item xs={6}>
                            <ImageUploadField
                                imageUrl={form.image_url}
                                onUrlChange={url => set('image_url', url)}
                                token={token}
                                examId={examId}
                                size="medium"
                            />
                        </Grid>

                        <Grid item xs={12}>
                            <FormControl fullWidth>
                                <InputLabel>Тип изпит *</InputLabel>
                                <Select value={form.exam_type} label="Тип изпит *"
                                    onChange={e => set('exam_type', e.target.value)}>
                                    <MenuItem value="SCHEDULED">Насрочен</MenuItem>
                                    <MenuItem value="ON_DEMAND">По желание</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>

                        {form.exam_type === 'SCHEDULED' && (
                            <Grid item xs={12}>
                                <TextField fullWidth type="datetime-local" label="Начална дата *"
                                    InputLabelProps={{ shrink: true }}
                                    value={form.start_date}
                                    onChange={e => set('start_date', e.target.value)} />
                            </Grid>
                        )}

                        <Grid item xs={12}>
                            <GuidelinesEditor guidelines={form.guidelines}
                                onChange={g => set('guidelines', g)} />
                        </Grid>

                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            <Typography variant="h6">Страница на изпита</Typography>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth label="Какво обхваща" value={form.coverage_summary}
                                placeholder="Материал от първи срок"
                                helperText="Кратка фраза без точка — показва се под „Какво обхваща?“, в ЧЗВ, в изскачащия прозорец за покупка и в последния призив"
                                onChange={e => set('coverage_summary', e.target.value)} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth multiline minRows={2} label="Какво представлява пробната матура?"
                                value={form.what_is_text}
                                onChange={e => set('what_is_text', e.target.value)} />
                        </Grid>
                        <Grid item xs={12}>
                            <TextListEditor items={form.highlights} onChange={h => set('highlights', h)}
                                title="Акценти (до текста „Какво представлява“)" itemLabel="Акцент" addLabel="Добави акцент" />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField fullWidth multiline minRows={2} label="SEO описание (meta description)"
                                value={form.meta_description}
                                helperText="Празно = използва се описанието"
                                onChange={e => set('meta_description', e.target.value)} />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={<Checkbox checked={form.has_raffle} onChange={e => set('has_raffle', e.target.checked)} />}
                                label="Томбола за сборник"
                            />
                        </Grid>
                        {[form.instructor_id, ...form.grader_ids.filter(id => id !== form.instructor_id)]
                            .map(id => instructors.find(inst => inst.id === id))
                            .filter(Boolean)
                            .map(inst => (
                                <Grid item xs={12} key={inst.id}>
                                    <TextField fullWidth multiline minRows={3}
                                        label={`Био на страницата — ${inst.first_name} ${inst.last_name}`}
                                        value={form.instructor_bios[String(inst.id)] || ''}
                                        helperText="Празно = описанието от профила. Празен ред разделя абзаците."
                                        onChange={e => set('instructor_bios', { ...form.instructor_bios, [String(inst.id)]: e.target.value })} />
                                    <FormControl fullWidth sx={{ mt: 2 }}>
                                        <InputLabel>{`Курс за консултация — ${inst.first_name} ${inst.last_name}`}</InputLabel>
                                        <Select
                                            value={form.instructor_courses[String(inst.id)] || ''}
                                            label={`Курс за консултация — ${inst.first_name} ${inst.last_name}`}
                                            onChange={e => set('instructor_courses', { ...form.instructor_courses, [String(inst.id)]: e.target.value || null })}
                                        >
                                            <MenuItem value=""><em>Без курс (води към профила на преподавателя)</em></MenuItem>
                                            {courses.filter(course => course.instructor_id === inst.id).map(course => (
                                                <MenuItem key={course.id} value={course.id}>
                                                    {course.title}{course.is_published ? '' : ' (неактивен)'}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            ))}

                        <Grid item xs={12}>
                            <Divider sx={{ my: 1 }} />
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="h6">Раздели</Typography>
                                <Button startIcon={<AddIcon />} onClick={addSection} variant="outlined" size="small">
                                    Добави раздел
                                </Button>
                            </Box>
                            {form.sections.map((s, i) => (
                                <SectionEditor key={s._key} section={s}
                                    onUpdate={updated => updateSection(i, updated)}
                                    onRemove={() => removeSection(i)}
                                    token={token}
                                    examId={examId} />
                            ))}
                        </Grid>

                        {error && (
                            <Grid item xs={12}>
                                <Typography color="error">{error}</Typography>
                            </Grid>
                        )}
                    </Grid>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Откажи</Button>
                <LoadingButton loading={loading} variant="contained" onClick={handleSave}>
                    {examId ? 'Запази' : 'Създай'}
                </LoadingButton>
            </DialogActions>
        </Dialog>
    );
}

// ---------------------------------------------------------------------------
// Sessions management (manual assignment for testing)
// ---------------------------------------------------------------------------

function SessionsDialog({ open, examId, examTitle, token, onClose }) {
    const [sessions, setSessions] = useState(null);
    const [students, setStudents] = useState([]);
    const [selectedStudent, setSelectedStudent] = useState(null);
    const [assigning, setAssigning] = useState(false);
    const [error, setError] = useState('');

    const loadSessions = useCallback(() => {
        if (!examId) return;
        fetch(`${globals.apiUrl}/admin/exams/${examId}/sessions`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(r => r.json())
            .then(data => setSessions(Array.isArray(data) ? data : []))
            .catch(() => setSessions([]));
    }, [examId, token]);

    useEffect(() => {
        if (!open) return;
        setSessions(null);
        setSelectedStudent(null);
        setError('');
        loadSessions();
        fetch(`${globals.apiUrl}/admin/users`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(r => r.json())
            .then(data => {
                const list = Array.isArray(data) ? data : [];
                setStudents(list.filter(u => String(u.role).toLowerCase().includes('student')));
            })
            .catch(() => setStudents([]));
    }, [open, loadSessions, token]);

    const handleAssign = async () => {
        if (!selectedStudent) { setError('Избери ученик'); return; }
        setError('');
        setAssigning(true);
        try {
            const res = await fetch(`${globals.apiUrl}/admin/exams/${examId}/sessions`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
                body: JSON.stringify({ student_id: selectedStudent.id }),
            });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                setError(err.detail || 'Грешка при задаване');
            } else {
                setSelectedStudent(null);
                loadSessions();
            }
        } catch {
            setError('Грешка при свързване');
        }
        setAssigning(false);
    };

    const handleDelete = async (sessionId) => {
        await fetch(`${globals.apiUrl}/admin/exams/sessions/${sessionId}`, {
            method: 'DELETE',
            headers: { Authorization: `Bearer ${token}` },
        });
        loadSessions();
    };

    const statusLabel = (s) => {
        if (s.has_submission) {
            if (s.grading_status === 'graded') return 'Оценен';
            if (s.grading_status === 'in_progress') return 'Оценяване';
            return 'Предаден';
        }
        if (s.has_draft) return 'Започнат';
        return 'Не е започнат';
    };

    const statusColor = (s) => {
        if (s.grading_status === 'graded') return 'success';
        if (s.has_submission) return 'warning';
        if (s.has_draft) return 'info';
        return 'default';
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Сесии — {examTitle}</DialogTitle>
            <DialogContent dividers>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>Задай сесия на ученик</Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start', mb: 1 }}>
                    <Autocomplete
                        sx={{ flex: 1 }}
                        size="small"
                        options={students}
                        value={selectedStudent}
                        onChange={(_, v) => setSelectedStudent(v)}
                        getOptionLabel={(o) => `${o.first_name || ''} ${o.last_name || ''} (${o.email})`.trim()}
                        isOptionEqualToValue={(o, v) => o.id === v.id}
                        renderInput={(params) => <TextField {...params} label="Ученик" placeholder="Търси по име или имейл" />}
                    />
                    <LoadingButton loading={assigning} variant="contained" onClick={handleAssign} sx={{ mt: 0.25 }}>
                        Задай
                    </LoadingButton>
                </Box>
                {error && <Typography color="error" variant="body2" sx={{ mb: 1 }}>{error}</Typography>}

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle2" sx={{ mb: 1 }}>Съществуващи сесии</Typography>
                {sessions === null ? (
                    <LinearProgress />
                ) : sessions.length === 0 ? (
                    <Typography variant="body2" color="text.secondary">Няма зададени сесии.</Typography>
                ) : (
                    sessions.map(s => {
                        const url = `${window.location.origin}/exams/${examId}/${s.session_id}`;
                        return (
                            <Paper key={s.session_id} variant="outlined" sx={{ p: 1.5, mb: 1 }}>
                                <Grid container alignItems="center" spacing={1}>
                                    <Grid item xs={12} sm={5}>
                                        <Typography sx={{ fontWeight: 'bold' }}>{s.student_name}</Typography>
                                        <Typography variant="caption" color="text.secondary">{s.student_email}</Typography>
                                    </Grid>
                                    <Grid item xs={6} sm={3}>
                                        <Chip size="small" label={statusLabel(s)} color={statusColor(s)} />
                                        {s.score != null && (
                                            <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>{s.score} т.</Typography>
                                        )}
                                    </Grid>
                                    <Grid item xs={4} sm={3}>
                                        <Link href={url} target="_blank" rel="noopener" variant="body2">Отвори сесията</Link>
                                    </Grid>
                                    <Grid item xs={2} sm={1} textAlign="right">
                                        <IconButton size="small" color="error" onClick={() => handleDelete(s.session_id)}>
                                            <DeleteIcon fontSize="small" />
                                        </IconButton>
                                    </Grid>
                                </Grid>
                            </Paper>
                        );
                    })
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Затвори</Button>
            </DialogActions>
        </Dialog>
    );
}

// ---------------------------------------------------------------------------
// Main page
// ---------------------------------------------------------------------------

const ExamsPage = () => {
    const { token } = useAuth();
    const [exams, setExams] = useState([]);
    const [filter, setFilter] = useState('');
    const [radioFilter, setRadioFilter] = useState('all');
    const [loadingStates, setLoadingStates] = useState({});
    const [deleteDialog, setDeleteDialog] = useState(null);
    const [formDialog, setFormDialog] = useState({ open: false, examId: null });
    const [previewDialog, setPreviewDialog] = useState({ open: false, examId: null });
    const [sessionsDialog, setSessionsDialog] = useState({ open: false, examId: null, examTitle: '' });

    const setLoading = (key, val) => setLoadingStates(p => ({ ...p, [key]: val }));

    const fetchExams = useCallback(() => {
        fetch(`${globals.apiUrl}/admin/exams`, {
            headers: { Authorization: `Bearer ${token}` },
        })
            .then(r => r.json())
            .then(setExams)
            .catch(console.error);
    }, [token]);

    useEffect(() => { fetchExams(); }, [fetchExams]);

    const handlePublish = async (id) => {
        setLoading(`${id}-publish`, true);
        await fetch(`${globals.apiUrl}/admin/exams/${id}/publish`, {
            method: 'POST', headers: { Authorization: `Bearer ${token}` },
        });
        setExams(prev => prev.map(e => e.id === id ? { ...e, is_published: true } : e));
        setLoading(`${id}-publish`, false);
    };

    const handleDraft = async (id) => {
        setLoading(`${id}-draft`, true);
        await fetch(`${globals.apiUrl}/admin/exams/${id}/draft`, {
            method: 'POST', headers: { Authorization: `Bearer ${token}` },
        });
        setExams(prev => prev.map(e => e.id === id ? { ...e, is_published: false } : e));
        setLoading(`${id}-draft`, false);
    };

    const handleSoldOut = async (id, soldOut) => {
        setLoading(`${id}-sold-out`, true);
        await fetch(`${globals.apiUrl}/admin/exams/${id}/${soldOut ? 'sold-out' : 'available'}`, {
            method: 'POST', headers: { Authorization: `Bearer ${token}` },
        });
        setExams(prev => prev.map(e => e.id === id ? { ...e, is_sold_out: soldOut } : e));
        setLoading(`${id}-sold-out`, false);
    };

    const handleDelete = async (id) => {
        setLoading(`${id}-delete`, true);
        await fetch(`${globals.apiUrl}/admin/exams/${id}`, {
            method: 'DELETE', headers: { Authorization: `Bearer ${token}` },
        });
        setExams(prev => prev.filter(e => e.id !== id));
        setLoading(`${id}-delete`, false);
        setDeleteDialog(null);
    };

    const filtered = exams
        .filter(e => e.title.toLowerCase().includes(filter.toLowerCase()))
        .filter(e => {
            if (radioFilter === 'active') return e.is_published;
            if (radioFilter === 'draft') return !e.is_published;
            return true;
        });

    const typeLabel = (t) => t === 'ON_DEMAND' ? 'По желание' : 'Насрочен';

    return (
        <Container>
            <Grid container sx={{ mb: 2 }} alignItems="center" justifyContent="space-between">
                <Grid item>
                    <Typography sx={{ fontWeight: 'bold', fontSize: 25, textTransform: 'uppercase' }}>
                        Изпити
                    </Typography>
                </Grid>
                <Grid item>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => setFormDialog({ open: true, examId: null })}
                        sx={{ backgroundColor: globals.colors.darkBlue }}
                    >
                        Нов изпит
                    </Button>
                </Grid>
            </Grid>

            <Grid container sx={{ mb: 2 }} alignItems="center" spacing={2}>
                <Grid item>
                    <Box sx={{ display: 'flex', alignItems: 'flex-end' }}>
                        <SearchIcon fontSize="large" sx={{ color: 'action.active', mr: 1, my: 0.5 }} />
                        <TextField placeholder="Търсене" size="small"
                            onChange={e => setFilter(e.target.value)} />
                    </Box>
                </Grid>
                <Grid item>
                    <FormControl onChange={e => setRadioFilter(e.target.value)}>
                        <RadioGroup defaultValue="all" row>
                            <FormControlLabel value="all" control={<Radio size="small" />} label="Всички" />
                            <FormControlLabel value="active" control={<Radio size="small" />} label="Активни" />
                            <FormControlLabel value="draft" control={<Radio size="small" />} label="Чернова" />
                        </RadioGroup>
                    </FormControl>
                </Grid>
            </Grid>

            <Grid container spacing={1}>
                {filtered.map(exam => (
                    <Grid item xs={12} key={exam.id}>
                        <Accordion>
                            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                    <Typography><strong>{exam.title}</strong></Typography>
                                    <Typography sx={{ color: 'text.secondary' }}>| {exam.instructor_name}</Typography>
                                    {exam.grader_names?.length > 0 && (
                                        <Typography sx={{ color: 'text.secondary' }}>
                                            | Оценяващи: {exam.grader_names.join(', ')}
                                        </Typography>
                                    )}
                                    <Chip
                                        size="small"
                                        label={exam.is_published ? 'Активен' : 'Чернова'}
                                        color={exam.is_published ? 'success' : 'default'}
                                    />
                                    {exam.is_sold_out && <Chip size="small" label="Изчерпан" color="warning" />}
                                    {exam.subject && <Chip size="small" label={SUBJECT_LABELS[exam.subject]} variant="outlined" />}
                                    <Chip size="small" label={typeLabel(exam.exam_type)} variant="outlined" />
                                    {exam.start_date && (
                                        <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                            {new Date(exam.start_date).toLocaleDateString('bg-BG')}
                                        </Typography>
                                    )}
                                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                                        {exam.sessions_count} покупки
                                    </Typography>
                                </Stack>
                            </AccordionSummary>
                            <AccordionActions>
                                <LoadingButton size="small" loading={loadingStates[`${exam.id}-publish`]}
                                    onClick={() => handlePublish(exam.id)}>
                                    Публикувай
                                </LoadingButton>
                                <LoadingButton size="small" loading={loadingStates[`${exam.id}-draft`]}
                                    onClick={() => handleDraft(exam.id)}>
                                    Чернова
                                </LoadingButton>
                                <LoadingButton size="small" color="warning" loading={loadingStates[`${exam.id}-sold-out`]}
                                    onClick={() => handleSoldOut(exam.id, !exam.is_sold_out)}>
                                    {exam.is_sold_out ? 'Отвори продажби' : 'Изчерпан'}
                                </LoadingButton>
                                <Button size="small" variant="outlined" startIcon={<VisibilityIcon />}
                                    onClick={() => setPreviewDialog({ open: true, examId: exam.id })}>
                                    Преглед
                                </Button>
                                <Button size="small" variant="outlined"
                                    onClick={() => setSessionsDialog({ open: true, examId: exam.id, examTitle: exam.title })}>
                                    Сесии
                                </Button>
                                <Button size="small" variant="contained" color="secondary"
                                    onClick={() => setFormDialog({ open: true, examId: exam.id })}>
                                    Редактирай
                                </Button>
                                <LoadingButton size="small" variant="contained" color="error"
                                    loading={loadingStates[`${exam.id}-delete`]}
                                    onClick={() => setDeleteDialog(exam.id)}>
                                    Изтрий
                                </LoadingButton>
                            </AccordionActions>
                        </Accordion>

                        <Dialog open={deleteDialog === exam.id} onClose={() => setDeleteDialog(null)}>
                            <DialogTitle>Искате ли да изтриете "{exam.title}"?</DialogTitle>
                            <DialogActions>
                                <Button onClick={() => { handleDelete(exam.id); }}>Да</Button>
                                <Button onClick={() => setDeleteDialog(null)} autoFocus>Не</Button>
                            </DialogActions>
                        </Dialog>
                    </Grid>
                ))}

                {filtered.length === 0 && (
                    <Grid item xs={12}>
                        <Typography sx={{ color: 'text.secondary', mt: 2 }}>Няма намерени изпити.</Typography>
                    </Grid>
                )}
            </Grid>

            <ExamFormDialog
                open={formDialog.open}
                examId={formDialog.examId}
                token={token}
                onClose={() => setFormDialog({ open: false, examId: null })}
                onSaved={fetchExams}
            />

            <ExamPreviewDialog
                open={previewDialog.open}
                examId={previewDialog.examId}
                token={token}
                onClose={() => setPreviewDialog({ open: false, examId: null })}
            />

            <SessionsDialog
                open={sessionsDialog.open}
                examId={sessionsDialog.examId}
                examTitle={sessionsDialog.examTitle}
                token={token}
                onClose={() => setSessionsDialog({ open: false, examId: null, examTitle: '' })}
            />
        </Container>
    );
};

export default ExamsPage;
